import { useEffect, useMemo, useState } from "react";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";
import { PRELEVEMENT_ENTITES } from "../Production/prelevementEntites";

import { useI18n } from "../i18n/I18nContext";

const TYPE_LABELS = {
  bois: "Bois",
  resine: "Résine",
  amorceur: "Amorceur",
};

const TYPE_META = {
  bois: { label: "Bois", className: "cpr-badge-bois" },
  resine: { label: "Résine", className: "cpr-badge-resine" },
  amorceur: { label: "Amorceur", className: "cpr-badge-amorceur" },
};

const ENTITE_META = {
  Prod: { cardClass: "cpr-card-prod", badgeClass: "cpr-badge-prod" },
  "R&D": { cardClass: "cpr-card-rd", badgeClass: "cpr-badge-rd" },
  "Indus 4.0": { cardClass: "cpr-card-indus", badgeClass: "cpr-badge-indus" },
};

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtQty(n, digits = 2) {
  const v = Number(n) || 0;
  return v.toLocaleString("fr-FR", {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
}

export default function ConsultationPrelevements() {
  const { t, ui } = useI18n();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [entiteFilter, setEntiteFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setErr("");
      try {
        const res = await apiFetch("/api/logistique/prelevements/recap");
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status === "error") {
          throw new Error(data.message || "Erreur chargement");
        }
        if (!cancelled) setRows(Array.isArray(data.items) ? data.items : []);
      } catch (e) {
        if (!cancelled) setErr(e.message || "Erreur de chargement");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const view = useMemo(() => {
    return rows.filter((r) => {
      if (typeFilter && r.type !== typeFilter) return false;
      if (entiteFilter && r.entite !== entiteFilter) return false;
      return true;
    });
  }, [rows, typeFilter, entiteFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter, entiteFilter]);

  const totalsByEntite = useMemo(() => {
    const map = {};
    for (const e of PRELEVEMENT_ENTITES) {
      map[e] = { bois: 0, resine: 0, amorceur: 0, count: 0 };
    }
    for (const r of view) {
      const key = r.entite || "Prod";
      if (!map[key]) map[key] = { bois: 0, resine: 0, amorceur: 0, count: 0 };
      map[key].count += 1;
      const q = Number(r.quantite) || 0;
      if (r.type === "bois") map[key].bois += q;
      if (r.type === "resine") map[key].resine += q;
      if (r.type === "amorceur") map[key].amorceur += q;
    }
    return map;
  }, [view]);

  const globalCount = view.length;
  const totalPages = Math.max(1, Math.ceil(view.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginated = view.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="cpr-page">
      <MenuBar active="logistique" />

      <div className="cpr-wrap">
        <div className="cpr-shell">
          <header className="cpr-header">
            <div className="cpr-header-kicker">{ui("Logistique")}</div>
            <h1 className="cpr-header-title">Récapitulatif des prélèvements</h1>
            <p className="cpr-header-sub">
              Qui a prélevé · quelle entité · bois / résine / amorceur
            </p>
          </header>

          <div className="cpr-body">
            <div className="cpr-stats-bar">
              <div className="cpr-stat-pill">
                <span className="cpr-stat-label">Lignes affichées</span>
                <strong className="cpr-stat-value">{globalCount}</strong>
              </div>
              <div className="cpr-stat-pill">
                <span className="cpr-stat-label">Entités</span>
                <strong className="cpr-stat-value">{PRELEVEMENT_ENTITES.length}</strong>
              </div>
            </div>

            <section className="cpr-section">
              <div className="cpr-section-label">Synthèse par entité</div>
              <div className="cpr-entite-grid">
                {PRELEVEMENT_ENTITES.map((e) => {
                  const t = totalsByEntite[e] || {};
                  const active = entiteFilter === e;
                  return (
                    <button
                      key={e}
                      type="button"
                      className={`cpr-entite-card ${ENTITE_META[e]?.cardClass || ""} ${active ? "cpr-entite-card-active" : ""}`}
                      onClick={() => setEntiteFilter(active ? "" : e)}
                    >
                      <div className="cpr-entite-card-top">
                        <span className="cpr-entite-name">{e}</span>
                        <span className="cpr-entite-count">{t.count || 0}</span>
                      </div>
                      <div className="cpr-entite-metrics">
                        <div>
                          <span>Bois</span>
                          <strong>{fmtQty(t.bois, 0)} plac.</strong>
                        </div>
                        <div>
                          <span>Résine</span>
                          <strong>{fmtQty(t.resine)} kg</strong>
                        </div>
                        <div>
                          <span>Amorceur</span>
                          <strong>{fmtQty(t.amorceur)} kg</strong>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="cpr-section">
              <div className="cpr-section-label">Filtres</div>
              <div className="cpr-filters">
                <div className="cpr-filter-group">
                  <span className="cpr-filter-label">Type</span>
                  <div className="cpr-chips">
                    {[
                      { id: "", label: "Tous" },
                      { id: "bois", label: "Bois" },
                      { id: "resine", label: "Résine" },
                      { id: "amorceur", label: "Amorceur" },
                    ].map((opt) => (
                      <button
                        key={opt.id || "all"}
                        type="button"
                        className={`cpr-chip ${typeFilter === opt.id ? "cpr-chip-active" : ""}`}
                        onClick={() => setTypeFilter(opt.id)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="cpr-filter-group">
                  <span className="cpr-filter-label">Entité</span>
                  <div className="cpr-chips">
                    <button
                      type="button"
                      className={`cpr-chip ${entiteFilter === "" ? "cpr-chip-active" : ""}`}
                      onClick={() => setEntiteFilter("")}
                    >
                      Toutes
                    </button>
                    {PRELEVEMENT_ENTITES.map((e) => (
                      <button
                        key={e}
                        type="button"
                        className={`cpr-chip ${entiteFilter === e ? "cpr-chip-active" : ""}`}
                        onClick={() => setEntiteFilter(e)}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                </div>

                {(typeFilter || entiteFilter) && (
                  <button
                    type="button"
                    className="cpr-reset"
                    onClick={() => {
                      setTypeFilter("");
                      setEntiteFilter("");
                    }}
                  >
                    {ui("Réinitialiser")}
                  </button>
                )}
              </div>
            </section>

            <section className="cpr-section cpr-section-table">
              <div className="cpr-section-label">Historique détaillé</div>

              <div className="cpr-table-wrap">
                <table className="cpr-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Entité</th>
                      <th>Type</th>
                      <th>Opérateur</th>
                      <th>Lot / PN</th>
                      <th>Quantité</th>
                      <th>Détail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={7} className="cpr-empty">
                          Chargement des prélèvements…
                        </td>
                      </tr>
                    )}

                    {!loading &&
                      paginated.map((r, idx) => {
                        const typeMeta = TYPE_META[r.type] || {
                          label: TYPE_LABELS[r.type] || r.type,
                          className: "",
                        };
                        const entite = r.entite || "Prod";
                        return (
                          <tr key={`${r.type}-${r.pn}-${r.date}-${idx}`}>
                            <td className="cpr-date">{formatDateTime(r.date)}</td>
                            <td>
                              <span className={`cpr-badge ${ENTITE_META[entite]?.badgeClass || ""}`}>
                                {entite}
                              </span>
                            </td>
                            <td>
                              <span className={`cpr-badge ${typeMeta.className}`}>
                                {typeMeta.label}
                              </span>
                            </td>
                            <td className="cpr-operator">{r.operator || "—"}</td>
                            <td className="cpr-pn">{r.pnLot || r.pn || "—"}</td>
                            <td className="cpr-qty">
                              <strong>{fmtQty(r.quantite, r.unite === "placages" ? 0 : 2)}</strong>
                              <span>{r.unite}</span>
                            </td>
                            <td className="cpr-detail">{r.detail || "—"}</td>
                          </tr>
                        );
                      })}

                    {!loading && view.length === 0 && (
                      <tr>
                        <td colSpan={7} className="cpr-empty">
                          Aucun prélèvement pour ces filtres
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {!loading && view.length > 0 && (
                <div className="cpr-pagination">
                  <button
                    type="button"
                    className="cpr-page-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  >
                    ← Préc.
                  </button>
                  <span className="cpr-page-info">
                    Page {currentPage} / {totalPages}
                    <em>
                      {startIndex + 1}–{Math.min(startIndex + itemsPerPage, view.length)} sur{" "}
                      {view.length}
                    </em>
                  </span>
                  <button
                    type="button"
                    className="cpr-page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Suiv. →
                  </button>
                </div>
              )}
            </section>

            {err && <div className="cpr-error">⚠ {err}</div>}
          </div>
        </div>
      </div>

      <Style />
    </div>
  );
}

function Style() {
  return (
    <style>{`
      .cpr-page {
        min-height: 100vh;
        width: 100%;
        background: radial-gradient(ellipse at top, #01283a 0%, #010F14 70%);
        color: #fff;
        font-family: Inter, "Segoe UI", system-ui, sans-serif;
      }
      .cpr-wrap {
        width: 100%;
        box-sizing: border-box;
        padding: 24px 48px 48px;
      }
      .cpr-shell {
        width: 100%;
        max-width: none;
        background: #011e2b;
        border: 1px solid #1a4a5a;
        border-radius: 16px;
        overflow: hidden;
        box-shadow: 0 12px 48px rgba(0,0,0,0.45);
      }
      .cpr-header {
        background: linear-gradient(135deg, #E6FF3E, #c8e800);
        color: #000;
        padding: 28px 40px;
        text-align: left;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
      }
      .cpr-header-kicker {
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        opacity: 0.65;
      }
      .cpr-header-title {
        margin: 0;
        font-size: 28px;
        font-weight: 800;
        letter-spacing: 0.04em;
        text-transform: uppercase;
      }
      .cpr-header-sub {
        margin: 0;
        font-size: 14px;
        font-weight: 500;
        opacity: 0.7;
      }
      .cpr-body {
        padding: 24px 32px 36px;
        display: flex;
        flex-direction: column;
        gap: 20px;
      }
      .cpr-stats-bar {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
      }
      .cpr-stat-pill {
        display: flex;
        flex-direction: column;
        gap: 2px;
        background: #012836;
        border: 1px solid #1a5a6a;
        border-radius: 12px;
        padding: 12px 18px;
        min-width: 160px;
      }
      .cpr-stat-label {
        font-size: 11px;
        color: #7ab8cc;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        font-weight: 700;
      }
      .cpr-stat-value {
        font-size: 24px;
        color: #E6FF3E;
        font-weight: 800;
      }
      .cpr-section {
        background: #012030;
        border: 1px solid #1a4a5a;
        border-radius: 14px;
        padding: 18px 20px;
      }
      .cpr-section-label {
        font-size: 13px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: #7ab8cc;
        margin-bottom: 14px;
      }
      .cpr-entite-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 16px;
      }
      .cpr-entite-card {
        text-align: left;
        background: #011820;
        border: 1px solid #1a4a5a;
        border-radius: 12px;
        padding: 14px;
        cursor: pointer;
        color: inherit;
        transition: border-color 0.15s, transform 0.15s, background 0.15s;
      }
      .cpr-entite-card:hover {
        transform: translateY(-1px);
        border-color: #2a6a7a;
      }
      .cpr-entite-card-active {
        border-color: #E6FF3E;
        box-shadow: 0 0 0 1px rgba(230,255,62,0.25);
      }
      .cpr-entite-card-top {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;
      }
      .cpr-entite-name {
        font-size: 15px;
        font-weight: 800;
        color: #fff;
      }
      .cpr-entite-count {
        background: #E6FF3E;
        color: #000;
        font-weight: 800;
        font-size: 12px;
        min-width: 28px;
        height: 28px;
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0 8px;
      }
      .cpr-entite-metrics {
        display: grid;
        gap: 8px;
      }
      .cpr-entite-metrics > div {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        font-size: 12px;
        color: #7ab8cc;
      }
      .cpr-entite-metrics strong {
        color: #dff8ff;
        font-weight: 700;
      }
      .cpr-card-prod { border-top: 3px solid #E6FF3E; }
      .cpr-card-rd { border-top: 3px solid #7aff9a; }
      .cpr-card-indus { border-top: 3px solid #7ab8cc; }
      .cpr-filters {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .cpr-filter-group {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .cpr-filter-label {
        font-size: 11px;
        font-weight: 700;
        color: #5a8a99;
        text-transform: uppercase;
        letter-spacing: 0.06em;
      }
      .cpr-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }
      .cpr-chip {
        background: #011820;
        color: #7ab8cc;
        border: 1px solid #1a4a5a;
        border-radius: 999px;
        padding: 7px 14px;
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s;
      }
      .cpr-chip:hover { background: #1a4a5a; color: #fff; }
      .cpr-chip-active {
        background: #E6FF3E;
        color: #000;
        border-color: #E6FF3E;
      }
      .cpr-reset {
        align-self: flex-start;
        background: transparent;
        color: #E6FF3E;
        border: 1px solid #3a5a20;
        border-radius: 8px;
        padding: 8px 12px;
        font-weight: 700;
        font-size: 12px;
        cursor: pointer;
      }
      .cpr-reset:hover { background: #1a2a00; }
      .cpr-table-wrap {
        overflow-x: auto;
        border-radius: 10px;
        border: 1px solid #1a4a5a;
      }
      .cpr-table {
        width: 100%;
        border-collapse: collapse;
        min-width: 0;
      }
      .cpr-table th {
        background: #013040;
        color: #E6FF3E;
        font-size: 11px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        padding: 12px 10px;
        text-align: left;
        border-bottom: 1px solid #1a4a5a;
        white-space: nowrap;
      }
      .cpr-table td {
        padding: 12px 10px;
        border-bottom: 1px solid #123848;
        font-size: 13px;
        color: #dff8ff;
        vertical-align: middle;
        background: #011820;
      }
      .cpr-table tbody tr:hover td {
        background: #012836;
      }
      .cpr-date {
        color: #7ab8cc;
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
      }
      .cpr-badge {
        display: inline-flex;
        align-items: center;
        padding: 4px 10px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0.03em;
        white-space: nowrap;
      }
      .cpr-badge-prod { background: rgba(230,255,62,0.15); color: #E6FF3E; }
      .cpr-badge-rd { background: rgba(122,255,154,0.15); color: #7aff9a; }
      .cpr-badge-indus { background: rgba(122,184,204,0.18); color: #9fd4e8; }
      .cpr-badge-bois { background: rgba(139,90,43,0.25); color: #e8c39a; }
      .cpr-badge-resine { background: rgba(0,176,80,0.18); color: #7aff9a; }
      .cpr-badge-amorceur { background: rgba(192,0,0,0.18); color: #ff9a9a; }
      .cpr-operator { font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; }
      .cpr-pn {
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
        font-size: 12px;
        color: #aaffcc;
        word-break: break-all;
      }
      .cpr-qty {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .cpr-qty strong { color: #E6FF3E; font-size: 14px; }
      .cpr-qty span { color: #5a8a99; font-size: 11px; text-transform: uppercase; }
      .cpr-detail { color: #7ab8cc; max-width: 220px; }
      .cpr-empty {
        text-align: center;
        padding: 28px 12px !important;
        color: #7ab8cc !important;
        font-weight: 600;
      }
      .cpr-pagination {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 14px;
        margin-top: 14px;
        flex-wrap: wrap;
      }
      .cpr-page-btn {
        background: #012836;
        color: #E6FF3E;
        border: 1px solid #1a4a5a;
        border-radius: 8px;
        padding: 8px 14px;
        font-weight: 700;
        font-size: 12px;
        cursor: pointer;
      }
      .cpr-page-btn:hover:not(:disabled) { background: #1a4a5a; color: #fff; }
      .cpr-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
      .cpr-page-info {
        color: #dff8ff;
        font-weight: 700;
        font-size: 13px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
      }
      .cpr-page-info em {
        font-style: normal;
        color: #5a8a99;
        font-size: 11px;
        font-weight: 600;
      }
      .cpr-error {
        background: #2a0808;
        border: 1px solid #C00000;
        color: #ff9090;
        border-radius: 10px;
        padding: 12px 14px;
        font-weight: 600;
      }
    `}</style>
  );
}
